/**
 * Canvas element utility functions
 * Extracted from Canvas.jsx to reduce file size
 */
import { flushSync } from "react-dom";

/**
 * Handles text transform updates
 */
export const handleTransformUpdate = ({
  id,
  x,
  y,
  width,
  height,
  rotation,
  setTexts,
  setSelectedText,
}) => {
  setTexts((prev) =>
    prev.map((t) =>
      t.id === id
        ? {
            ...t,
            x,
            y,
            width,
            height,
            rotation,
          }
        : t
    )
  );

  setSelectedText((prev) =>
    prev?.id === id
      ? {
          ...prev,
          x,
          y,
          width,
          height,
          rotation,
        }
      : prev
  );
};

/**
 * Handles image click events, selecting the image and attaching the transformer.
 * @param {string} imageId - The ID of the clicked image.
 * @param {Event} event - The click event (optional, used to check for shift key for multi-select)
 */
export const handleImageClick = ({
  imageId,
  images,
  setSelectedImage,
  setSelectedObject,
  setImages,
  stageRef,
  transformerRef,
  event = null,
  selectedObjects = [],
  setSelectedObjects = null,
  isMultiSelectMode = false,
  selectedObject = null,
  setSelectedText = null,
  setShowTextBox = null,
  setSelectedArrow = null,
  setSelectedDoubleSidedArrow = null,
  setSelectedLine = null,
}) => {
  const selectedImg = images.find((img) => img.id === imageId);

  // CRITICAL: Two-click behavior when multi-select is disabled — if another element is selected,
  // first click only clears selection; second click selects this image (same as rect/circle/text).
  const hasDifferentSelectedObject =
    !!selectedObject?.id && selectedObject.id !== imageId;
  const hasDifferentSelectedInArray =
    Array.isArray(selectedObjects) &&
    selectedObjects.some((obj) => obj?.id && obj.id !== imageId);

  if (!isMultiSelectMode && (hasDifferentSelectedObject || hasDifferentSelectedInArray)) {
    if (setSelectedObjects) setSelectedObjects([]);
    setSelectedObject(null);
    setSelectedImage(null);
    if (setSelectedText) setSelectedText(null);
    if (setShowTextBox) setShowTextBox(false);
    if (setSelectedArrow) setSelectedArrow(null);
    if (setSelectedDoubleSidedArrow) setSelectedDoubleSidedArrow(null);
    if (setSelectedLine) setSelectedLine(null);
    return;
  }

  // CRITICAL FIX: Check if this is a multi-select operation (shift key or multi-select mode)
  const isShiftClick = event?.evt?.shiftKey || event?.shiftKey;
  const isMultiSelect = isShiftClick || isMultiSelectMode;

  // CRITICAL: In multi-select mode, add to selectedObjects instead of replacing
  if (isMultiSelect && setSelectedObjects) {
    const currentSelection =
      selectedObjects && selectedObjects.length > 0
        ? [...selectedObjects]
        : selectedObject
          ? [{ ...selectedObject, type: selectedObject.type || "Image" }]
          : [];

    // Check if image is already selected (including selectedObject fallback)
    const isAlreadySelected = currentSelection.some(
      (obj) => obj.id === imageId && obj.type === "Image"
    );
    
    if (isAlreadySelected) {
      // Remove from selectedObjects (toggle off)
      const updated = currentSelection.filter(
        (obj) => !(obj.id === imageId && obj.type === "Image")
      );
      const nextPrimary = updated[0] || null;
      setSelectedObjects(updated);
      setSelectedObject(nextPrimary);
      setSelectedImage(nextPrimary?.type === "Image" ? nextPrimary : null);
    } else {
      // Add to selectedObjects
      if (selectedImg) {
        const newSelected = {
          id: imageId,
          type: "Image",
          x: selectedImg.x,
          y: selectedImg.y,
          width: selectedImg.width,
          height: selectedImg.height,
          maintainAspectRatio: selectedImg.maintainAspectRatio,
          compress: selectedImg.compress,
          ...selectedImg,
        };
        setSelectedObjects([...currentSelection, newSelected]);
        if (!selectedObject) {
          setSelectedObject(newSelected);
        }
        setSelectedImage(selectedImg);
      }
    }

    return;
  }
  
  // Normal single-select mode
  setSelectedImage(selectedImg);

  if (selectedImg && setSelectedObject) {
    setSelectedObject({
      id: imageId,
      type: "Image",
      x: selectedImg.x,
      y: selectedImg.y,
      width: selectedImg.width,
      height: selectedImg.height,
      maintainAspectRatio: selectedImg.maintainAspectRatio,
      compress: selectedImg.compress,
      ...selectedImg,
    });
  }

  setImages((prevImages) =>
    prevImages.map((img) => ({
      ...img,
      isSelected: img.id === imageId,
    }))
  );

  // Attach transformer to selected image
  if (transformerRef?.current) {
    const imageNode = stageRef.current?.findOne(`#${imageId}`);
    if (imageNode) {
      // Ensure node dimensions match state before attaching transformer
      const img = images.find((img) => img.id === imageId);
      if (img) {
        imageNode.width(img.width);
        imageNode.height(img.height);

        // CRITICAL FIX: Calculate scale correctly for flip transformations
        // For local blobs with hasBakedTransforms, flips are baked into pixels
        // For API images (S3 URLs), flips are NEVER baked - S3 contains original
        const isApiImage =
          img.src &&
          (img.src.startsWith("http://") || img.src.startsWith("https://"));
        const shouldApplyFlipViaScale = !img.hasBakedTransforms || isApiImage;

        const calculatedScaleX =
          img.flipHorizontal && shouldApplyFlipViaScale
            ? -Math.abs(img.scaleX || 1)
            : Math.abs(img.scaleX || 1);
        const calculatedScaleY =
          img.flipVertical && shouldApplyFlipViaScale
            ? -Math.abs(img.scaleY || 1)
            : Math.abs(img.scaleY || 1);

        imageNode.scaleX(calculatedScaleX);
        imageNode.scaleY(calculatedScaleY);
      }

      transformerRef.current.nodes([imageNode]);
      transformerRef.current.getLayer().batchDraw();
    }
  }
};

/**
 * Handles image resize events.
 * @param {string} id - The ID of the resized image.
 * @param {number} width - The new width.
 * @param {number} height - The new height.
 */
export const handleImageResize = ({
  id,
  width,
  height,
  setImages,
  saveToHistory,
}) => {
  setImages((prevImages) =>
    prevImages.map((img) =>
      img.id === id
        ? {
            ...img,
            width,
            height,
          }
        : img
    )
  );
  saveToHistory();
};

/**
 * Handles part click in text elements
 */
export const handlePartClick = ({
  clickedPart,
  typeKey,
  isNote,
  textId,
  setEditTextBoxContent,
  setSelectedObject,
  setShowEditTask,
  setShowEditNote,
  setShowTextBox,
  setTexts,
  setSelectedText,
  setShowQtyUnitOptions = null, // Optional: clear edit item code when opening edit note
}) => {

  const contentString = `${clickedPart.part} - Qty-${clickedPart.qty} ${clickedPart.unit}`;

  // Don't set editTextBoxContent=true here - we'll set it to false after selectedContent is set
  // This prevents the flash of edit content view
  // CRITICAL: Don't set showEditNote here - we'll set it synchronously in flushSync based on isNote parameter
  // Setting it here before flushSync can cause race conditions
  // CRITICAL: Don't clear selectedObject here - we need it set to the text element for allowDbl() to work
  // We'll set it properly in flushSync along with other state
  setShowEditTask(false);
  setShowTextBox(true);

  // CRITICAL: Use flushSync to ensure selectedContent updates are applied BEFORE setting editTextBoxContent=false
  // This prevents TextBoxWithButtons from rendering with editTextBoxContent=false but selectedContent=null

  // CRITICAL: Update both texts and selectedText in the same flushSync to ensure consistency
  // Use a helper to build the new selectedContent once
  const newSelectedContent = {
    type: "part",
    content: contentString,
    index: clickedPart.indexInType,
    part: clickedPart,
    qty: clickedPart.qty,
    unit: clickedPart.unit,
    typeKey,
    id: clickedPart.id,
    part_ref: clickedPart.part_ref,
    unit_type_id: clickedPart.unit_type_id, // Add unit_type_id for proper unit filtering
  };

  flushSync(() => {
    // CRITICAL: When opening edit note, clear edit item code section in same sync batch
    if (isNote && setShowQtyUnitOptions) {
      setShowQtyUnitOptions(false);
    }
    // Update texts array
    setTexts((prevTexts) => {
      // CRITICAL: Read the current text from prevTexts to get the latest selectedContent
      // Don't rely on the text prop which might be stale
      const currentText = prevTexts.find((t) => t.id === textId);
      const currentSelectedContent = currentText?.selectedContent;


      // CRITICAL: If currentSelectedContent is null, it means something cleared it
      // But we should still set the NEW selectedContent - don't let null prevent us from setting the new part
      if (!currentSelectedContent) {
        console.warn(
          "⚠️ [handlePartClick] WARNING: Current selectedContent is null! Setting new selectedContent anyway.",
          {
            textId,
            newPartId: newSelectedContent.id,
          }
        );
      }

      const updated = prevTexts.map((t) =>
        t.id === textId
          ? {
              ...t,
              selectedContent: {
                // CRITICAL: Always set the NEW selectedContent regardless of what was there before
                // Even if currentSelectedContent is null, we still want to set the new part
                ...newSelectedContent, // This is the NEW part selection - it should completely replace whatever was there
              },
            }
          : t
      );
      const updatedText = updated.find((t) => t.id === textId);

      // CRITICAL: Update selectedText to match the updated text from texts array
      // This ensures selectedText always has the latest state
      // CRITICAL: Always use updatedText from texts array to ensure we have the correct selectedContent
      // Don't rely on prev.selectedContent which might be null or stale
      setSelectedText((prev) => {
        // Always use the updatedText from texts array - it has the correct selectedContent
        // This ensures selectedText matches what's in texts array
        if (!updatedText) {
          console.warn("⚠️ [handlePartClick] No updatedText found!");
          return prev;
        }

        // CRITICAL: Always use updatedText which has the new selectedContent
        // Don't try to preserve prev.selectedContent - use the one from texts array
        const updatedSelectedText = {
          ...updatedText, // This already has the correct selectedContent with the new part
        };

        return updatedSelectedText;
      });

      // CRITICAL: Set showEditNote synchronously within flushSync
      // This ensures the view state is set immediately along with selectedContent
      if (isNote) {
        setShowEditNote(true);
      } else {
        setShowEditNote(false);
      }

      // CRITICAL: Set editTextBoxContent=false synchronously within flushSync
      // This ensures the view state is consistent
      setEditTextBoxContent(false);

      // CRITICAL: Ensure selectedObject is set to the text element
      // This is needed for allowDbl() to work when switching between part view and note view
      // If selectedObject is null, allowDbl() will block double-clicks
      if (updatedText) {
        setSelectedObject({
          id: textId,
          type: "Text",
          fontSize: updatedText.fontSize,
        });
      }

      return updated;
    });
  });


  // CRITICAL: Verify that selectedContent was actually set
  // Read back from texts array to confirm
  setTimeout(() => {
    // Use a small delay to let React flush the state
    setTexts((currentTexts) => {
      const verifyText = currentTexts.find((t) => t.id === textId);
      if (verifyText?.selectedContent?.id !== newSelectedContent.id) {
        console.error(
          "❌ [handlePartClick] VERIFICATION FAILED: selectedContent not set correctly!",
          {
            expectedId: newSelectedContent.id,
            actualId: verifyText?.selectedContent?.id,
            selectedContentIsNull: !verifyText?.selectedContent,
          }
        );
      } else {
      }
      return currentTexts; // Don't modify, just verify
    });
  }, 0);
};
